import { AxiosInstance } from 'axios';
import { IApiService, BailleurItem, LogItem, UserDetail, UpdateUserDetailRequest, CommuneItem, Logement, LogementRaw, LogItemRaw, WrappedResult } from './ApiService';
import { ApiHelpers } from './httpClient';
import { Logger } from '../utils/Logger';
import { trackEvent, setAuthenticatedUserContext, clearAuthenticatedUserContext } from './AppInsightsPlugin';
import axios from 'axios';

/**
 * References to auth state refs (managed by ApiServiceProvider)
 * These are read by the auth check methods (isAuthenticated, isCurrentUserBailleur, etc.)
 */
export interface AuthRefs {
  bailleurIdRef: React.RefObject<number | undefined>;
  isProfileCompleteRef: React.RefObject<boolean>;
}

/**
 * Auth state helper functions (managed by ApiServiceProvider)
 * These are called by login/logout/getUserDetail to update auth state
 */
export interface AuthActions {
  clearAuthState: (reason: string) => void;
  setAuthBailleurId: (id: number | undefined, reason: string) => void;
  updateProfileCompletion: (isComplete: boolean, reason: string) => void;
}

/**
 * Create the API service object with all methods
 *
 * This function composes the API methods from helpers and auth state.
 * The returned IApiService object is what gets provided to components.
 *
 * @param axiosClient - The configured axios instance (for login/resetPassword methods)
 * @param helpers - HTTP helper functions (apiGet, apiPost, apiDelete)
 * @param authRefs - References to auth state
 * @param authActions - Functions to update auth state
 * @returns IApiService implementation
 */
export function createApiService(
  axiosClient: AxiosInstance,
  helpers: ApiHelpers,
  authRefs: AuthRefs,
  authActions: AuthActions
): IApiService {
  return {
    // ============ AUTHENTICATION STATE METHODS ============
    isAuthenticated(): boolean {
      return authRefs.bailleurIdRef.current !== undefined;
    },

    isCurrentUserBailleur(): boolean {
      return ((authRefs.bailleurIdRef.current as number | undefined) ?? 0) > 0;
    },

    isCurrentUserHanditoit(): boolean {
      return ((authRefs.bailleurIdRef.current as number | undefined) ?? 0) === 0;
    },

    getCurrentBailleurId(): number | undefined {
      return (authRefs.bailleurIdRef.current as number | undefined) ?? undefined;
    },

    isProfileComplete(): boolean {
      return (authRefs.isProfileCompleteRef.current as boolean) ?? false;
    },

    // ============ ACCOUNT METHODS ============
    async login(email: string, password: string, abortSignal: AbortSignal): Promise<undefined | Readonly<Record<string, ReadonlyArray<string>>>> {
      let errors: undefined | Readonly<Record<string, ReadonlyArray<string>>> = undefined;
      let userIdFromResponse: string | undefined;

      // POST /api/Account/Login
      // Note: LoginStrategy intercepts this but does NOT trigger sync
      // Sync will be triggered by the context header interceptor → checkAndHandleContextChange()
      await axiosClient.post("Account/Login", {
        email: email,
        password: password
      }, {
        signal: abortSignal
      })
        .then(response => {
          // Capture userId from response header for AppInsights user context
          userIdFromResponse = response.headers['x-user-id'] as string | undefined;
          // Backend now returns isProfileComplete to avoid extra getUserDetail() call
          const isProfileComplete = response.data?.isProfileComplete ?? false;
          authActions.updateProfileCompletion(isProfileComplete, `login - isProfileComplete: ${isProfileComplete}`);
        })
        .catch(error => {
          if (axios.isAxiosError(error)) {
            if (error.response?.status === 400) {
              errors = error.response?.data.errors || error.response?.data;
              trackEvent('LOGIN_FAILED', {
                reason: 'validation_error'
              });
            } else if (!errors && !axios.isCancel(error)) {
              trackEvent('LOGIN_FAILED', {
                reason: 'api_error',
                status: error.response?.status?.toString()
              });
            }
          }
          if (!errors && !axios.isCancel(error)) {
            throw error;
          }
        });

      if (!errors) {
        trackEvent('LOGIN_SUCCESS');

        // ✅ Set anonymized user context for AppInsights bug correlation
        if (userIdFromResponse) {
          const userIdHash = btoa(userIdFromResponse).substring(0, 16); // Create anonymized hash
          setAuthenticatedUserContext(userIdHash);
          Logger.debug('ApiService', '✅ User context set for AppInsights');
        }

        // ✅ isProfileComplete is now returned directly from Login endpoint
        // No extra getUserDetail() call needed
      }

      return errors;
    },

    async logout(abortSignal: AbortSignal): Promise<void> {
      try {
        await helpers.apiPost("Account/Logout", {}, {
          signal: abortSignal
        });
      } finally {
        // Always reset authentication state, regardless of API call success or failure
        // Defensive approach: if logout was attempted, clear local auth state immediately
        // Better to be logged out locally than have stale auth state
        authActions.clearAuthState('logout() - finally block');
        trackEvent('LOGOUT');

        // ✅ Clear user context from AppInsights when logging out
        clearAuthenticatedUserContext();
        Logger.debug('ApiService', '✅ User context cleared from AppInsights');
      }
    },

    async resetPassword(email: string, password: string, confirmPassword: string, code: string, abortSignal: AbortSignal): Promise<undefined | Readonly<Record<string, ReadonlyArray<string>>>> {
      let errors: undefined | Readonly<Record<string, ReadonlyArray<string>>> = undefined;
      await axiosClient.post("Account/ResetPassword", {
        email: email,
        password: password,
        confirmPassword: confirmPassword,
        code: code
      }, {
        signal: abortSignal
      })
        .catch(error => {
          if (axios.isAxiosError(error)) {
            if (error.response?.status === 400) {
              errors = error.response?.data.errors || error.response?.data
            }
          }
          if (!errors && !axios.isCancel(error)) {
            throw error;
          }
        });
      return errors;
    },

    async sendResetPasswordEmail(email: string, abortSignal: AbortSignal): Promise<undefined | Readonly<Record<string, ReadonlyArray<string>>>> {
      let errors: undefined | Readonly<Record<string, ReadonlyArray<string>>> = undefined;
      await axiosClient.post("Account/ForgotPassword", {
        email: email
      }, {
        signal: abortSignal
      })
        .catch(error => {
          if (axios.isAxiosError(error)) {
            if (error.response?.status === 400) {
              errors = error.response?.data.errors || error.response?.data
            }
          }
          if (!errors && !axios.isCancel(error)) {
            throw error;
          }
        });
      return errors;
    },

    async getUserDetail(abortSignal: AbortSignal): Promise<UserDetail> {
      const data = await helpers.apiGet<UserDetail>("Account/UserDetail", {
        signal: abortSignal
      });
      if (!data) {
        throw new Error("Not found");
      }
      // Update profile completion state based on firstName/lastName
      const hasFullName = !!(data.firstName?.trim() && data.lastName?.trim());
      authActions.updateProfileCompletion(hasFullName, `getUserDetail - hasFullName: ${hasFullName}`);
      return data;
    },

    async setUserDetail(userDetail: UpdateUserDetailRequest, abortSignal: AbortSignal): Promise<void> {
      await helpers.apiPost("Account/UserDetail", userDetail, {
        signal: abortSignal
      });
      // Update profile completion state only if request wasn't aborted
      if (!abortSignal.aborted) {
        authActions.updateProfileCompletion(true, 'setUserDetail - profile updated');
      }
    },

    // ============ BAILLEURS METHODS ============
    async getBailleurs(abortSignal: AbortSignal): Promise<ReadonlyArray<BailleurItem>> {
      const data = await helpers.apiGet<WrappedResult<BailleurItem>>("Bailleurs", {
        signal: abortSignal
      });
      const items = data?.items;
      return items ?? [];
    },

    async getBailleur(bailleurId: number, abortSignal: AbortSignal): Promise<BailleurItem> {
      const bailleurs = await this.getBailleurs(abortSignal);
      const bailleurItems = bailleurs.filter(x => x.id === bailleurId);
      if (bailleurItems.length !== 1) {
        throw new Error("Not found");
      }
      return bailleurItems[0];
    },

    // ============ PROGRAMMES METHODS ============
    async getProgrammes(bailleurId: number, abortSignal: AbortSignal): Promise<ReadonlyArray<string>> {
      const data = await helpers.apiGet<WrappedResult<string>>(`Programmes/${bailleurId}`, {
        signal: abortSignal
      });
      const items = data?.items;
      return items ?? [];
    },

    // ============ LOGEMENTS METHODS ============
    async getLogements(bailleurId: number, programme: string | undefined, abortSignal: AbortSignal): Promise<ReadonlyArray<Logement>> {
      const p = programme ? "?" + new URLSearchParams({
        programme: programme
      }).toString() : "";
      const data = await helpers.apiGet<WrappedResult<LogementRaw>>(`Logements/${bailleurId}${p}`, {
        signal: abortSignal
      });
      const items = data?.items;
      return (items ?? []).map((x: LogementRaw) => {
        return {
          ...x,
          creationDate: new Date(Date.parse(x.creationDate)),
          lastModificationDate: new Date(Date.parse(x.lastModificationDate))
        }
      });
    },

    async getLogement(logementId: number, abortSignal: AbortSignal): Promise<Logement> {
      const data = await helpers.apiGet<LogementRaw>(`Logement/${logementId}`, {
        signal: abortSignal
      });
      if (!data) {
        throw new Error("Not found");
      }
      return {
        ...data,
        creationDate: new Date(Date.parse(data.creationDate)),
        lastModificationDate: new Date(Date.parse(data.lastModificationDate)),
        comment: "Mise à jour"
      };
    },

    async deleteLogement(logementId: number, abortSignal: AbortSignal): Promise<void> {
      await helpers.apiDelete(`Logement/${logementId}`, {
        signal: abortSignal
      });
    },

    async upsertLogement(logement: Logement, abortSignal: AbortSignal): Promise<{ logement: Logement; logEntry?: LogItemRaw } | undefined> {
      const data = await helpers.apiPost<{ logement: LogementRaw; logEntry?: LogItemRaw }, Logement>(
        `Logement/${logement.id}`, logement, { signal: abortSignal }
      );
      if (!data) return undefined;
      return {
        logement: {
          ...data.logement,
          creationDate: new Date(Date.parse(data.logement.creationDate)),
          lastModificationDate: new Date(Date.parse(data.logement.lastModificationDate)),
        },
        logEntry: data.logEntry,
      };
    },

    async getLogEntries(logementId: number, abortSignal: AbortSignal): Promise<ReadonlyArray<LogItem>> {
      const data = await helpers.apiGet<WrappedResult<LogItemRaw>>(`Logement/${logementId}/Logs`, {
        signal: abortSignal
      });
      if (!data) {
        throw new Error("Not found");
      }
      const items = data?.items;
      return (items ?? []).map((x: LogItemRaw) => {
        return {
          ...x,
          createdDate: new Date(Date.parse(x.createdDate))
        }
      });
    },

    async getDiagAccessiblitePdfUrl(logementId: number, version: number, abortSignal: AbortSignal): Promise<string | undefined> {
      const data = await helpers.apiGet<{ readonly url: string }>(`Logement/${logementId}/pdfUrl/${version}`, {
        signal: abortSignal
      });
      return data?.url;
    },

    // ============ COMMUNES METHODS ============
    async getCommunes(abortSignal: AbortSignal): Promise<ReadonlyArray<CommuneItem>> {
      const data = await helpers.apiGet<WrappedResult<CommuneItem>>("Utils/CommunesPACA", {
        signal: abortSignal
      });
      const items = data?.items;
      return items ?? [];
    }
  };
}
